import type { AudioManager } from "./AudioManager";
import type { HeroClass } from "./types";
import type { UI } from "./UI";

export interface TimedGameResult {
  misses: number;
}

export interface PackingResult {
  items: string[];
  suspicion: number;
  bonus: number;
}

export interface PackingItem {
  id: string;
  icon: string;
  name: string;
  detail: string;
  risk: number;
  bonus: number;
}

export class MiniGames {
  constructor(
    private readonly ui: UI,
    private readonly audio: AudioManager,
    private readonly afterClose: () => void,
  ) {}

  chooseHero(): Promise<HeroClass> {
    const body = this.ui.openPanel(
      "Choisis ton archétype",
      "Toute mauvaise décision mérite d’être spécialisée.",
    );
    body.innerHTML = `
      <div class="hero-grid">
        <button class="hero-card" data-hero="veteran">
          <span>🧔‍♂️</span><small>POLYVALENT</small><h3>Vétéran du 56k</h3>
          <p>Plus d’énergie. Se souvient du bruit d’un modem et refuse d’en parler.</p>
          <b>Second souffle · sang-froid douteux</b>
        </button>
        <button class="hero-card" data-hero="cpa">
          <span>🧾</span><small>DIALOGUE</small><h3>CPA du raid</h3>
          <p>Transforme toute file d’attente en contrôle interne documenté.</p>
          <b>+12 réputation · raccourci administratif</b>
        </button>
        <button class="hero-card" data-hero="goblin">
          <span>👺</span><small>CHAOS</small><h3>Gobelin de câble</h3>
          <p>Peut sentir une rallonge libre à travers deux murs de béton.</p>
          <b>Câble bonus · soupçon initial +6</b>
        </button>
      </div>
      <p class="panel-footnote">La classe influence quelques dialogues et le bilan final, mais aucune ne bloque la campagne.</p>
    `;

    return new Promise((resolve) => {
      body.querySelectorAll<HTMLButtonElement>("[data-hero]").forEach((button) => {
        button.onclick = () => {
          this.audio.play("success");
          const hero = button.dataset.hero as HeroClass;
          this.finish();
          resolve(hero);
        };
      });
    });
  }

  precisionParking(): Promise<TimedGameResult> {
    const body = this.ui.openPanel(
      "Créneau P-404",
      "Aligne l’angle, la distance et la dignité. Espace ou clic pour freiner.",
    );
    body.innerHTML = `
      <div class="parking-game">
        <div class="parking-stage">
          <small data-parking-step>ÉTAPE 1/3</small>
          <h3 data-parking-label>Angle d’approche</h3>
        </div>
        <div class="timing-track parking-track">
          <div class="timing-target" data-timing-target></div>
          <div class="timing-cursor car-cursor" data-timing-cursor>🚙</div>
        </div>
        <div class="parking-spaces"><i></i><b>P-404</b><i></i></div>
        <button class="button primary game-action" data-stop>FREIN À MAIN <kbd>ESPACE</kbd></button>
        <p class="game-feedback" data-feedback>La Civic derrière toi klaxonne déjà.</p>
      </div>
    `;

    const cursor = body.querySelector<HTMLElement>("[data-timing-cursor]")!;
    const target = body.querySelector<HTMLElement>("[data-timing-target]")!;
    const stepText = body.querySelector<HTMLElement>("[data-parking-step]")!;
    const label = body.querySelector<HTMLElement>("[data-parking-label]")!;
    const feedback = body.querySelector<HTMLElement>("[data-feedback]")!;
    const button = body.querySelector<HTMLButtonElement>("[data-stop]")!;
    const labels = ["Angle d’approche", "Distance du trottoir", "Regard de Martine"];
    const targets = [64, 36, 52];
    let stage = 0;
    let misses = 0;
    let active = true;
    let frame = 0;
    let started = performance.now();

    const draw = (now: number): void => {
      if (!active) return;
      const value = 50 + Math.sin((now - started) * (0.0022 + stage * 0.00035)) * 47;
      cursor.style.left = `${value}%`;
      frame = requestAnimationFrame(draw);
    };

    const stop = (): void => {
      if (!active) return;
      const value = Number.parseFloat(cursor.style.left);
      const targetValue = targets[stage];
      const difference = Math.abs(value - targetValue);
      if (difference <= 12) {
        this.audio.play("success");
        feedback.textContent =
          stage === 2
            ? "Martine hoche la tête. C’est pratiquement une médaille."
            : "CLONK. Étonnamment réglementaire.";
        stage += 1;
        if (stage >= labels.length) {
          active = false;
          cancelAnimationFrame(frame);
          window.removeEventListener("keydown", onKey);
          window.setTimeout(() => {
            this.finish();
            resolvePromise({ misses });
          }, 650);
          return;
        }
        stepText.textContent = `ÉTAPE ${stage + 1}/3`;
        label.textContent = labels[stage];
        started = performance.now();
        target.style.left = `${targets[stage] - 12}%`;
        target.style.width = "24%";
      } else {
        misses += 1;
        this.audio.play("error");
        feedback.textContent =
          difference > 32
            ? "Tu viens de stationner dans une autre circonscription."
            : "Presque. Le cône orange demande une seconde tentative.";
        started = performance.now();
      }
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.code === "Space" || event.code === "KeyE") {
        event.preventDefault();
        stop();
      }
    };
    let resolvePromise!: (value: TimedGameResult) => void;
    const promise = new Promise<TimedGameResult>((resolve) => {
      resolvePromise = resolve;
    });
    target.style.left = `${targets[0] - 12}%`;
    target.style.width = "24%";
    button.onclick = stop;
    window.addEventListener("keydown", onKey);
    frame = requestAnimationFrame(draw);
    return promise;
  }

  chargePhone(): Promise<TimedGameResult> {
    const body = this.ui.openPanel(
      "Réanimation du téléphone",
      "Mémorise l’ordre des ports. La batterie est à 3 %, donc aucune pression.",
    );
    const colors = [
      ["cyan", "CYAN"],
      ["lime", "VERT"],
      ["amber", "JAUNE"],
      ["violet", "MAUVE"],
    ] as const;
    body.innerHTML = `
      <div class="memory-game">
        <div class="memory-screen">
          <small>SÉQUENCE DE DÉMARRAGE</small>
          <strong data-memory-status>Observe…</strong>
        </div>
        <div class="port-grid">
          ${colors
            .map(
              ([color, label], index) =>
                `<button class="port ${color}" data-port="${index}"><i></i><span>${label}</span></button>`,
            )
            .join("")}
        </div>
        <div class="round-pips" data-round-pips><i></i><i></i><i></i></div>
        <p class="game-feedback" data-feedback>Le chargeur porte la mention rassurante « USB-ish ».</p>
      </div>
    `;

    const buttons = [...body.querySelectorAll<HTMLButtonElement>("[data-port]")];
    const status = body.querySelector<HTMLElement>("[data-memory-status]")!;
    const feedback = body.querySelector<HTMLElement>("[data-feedback]")!;
    const pips = [...body.querySelectorAll<HTMLElement>("[data-round-pips] i")];
    const sequences = [
      [1, 3, 0],
      [2, 0, 3, 1],
      [3, 1, 2, 0, 2],
    ];
    let round = 0;
    let input: number[] = [];
    let misses = 0;
    let accepting = false;
    let finished = false;

    const flashSequence = async (): Promise<void> => {
      accepting = false;
      input = [];
      status.textContent = "Observe…";
      buttons.forEach((button) => (button.disabled = true));
      await this.wait(450);
      for (const value of sequences[round]) {
        if (finished) return;
        buttons[value].classList.add("lit");
        this.audio.play("click");
        await this.wait(330);
        buttons[value].classList.remove("lit");
        await this.wait(130);
      }
      status.textContent = "À toi";
      buttons.forEach((button) => (button.disabled = false));
      accepting = true;
    };

    return new Promise((resolve) => {
      buttons.forEach((button) => {
        button.onclick = () => {
          if (!accepting) return;
          const value = Number(button.dataset.port);
          const expected = sequences[round][input.length];
          button.classList.add("lit");
          window.setTimeout(() => button.classList.remove("lit"), 160);
          if (value !== expected) {
            misses += 1;
            accepting = false;
            this.audio.play("error");
            feedback.textContent = "Écran bleu émotionnel. La même séquence recommence.";
            window.setTimeout(() => void flashSequence(), 700);
            return;
          }
          this.audio.play("click");
          input.push(value);
          if (input.length !== sequences[round].length) return;
          this.audio.play("success");
          pips[round].classList.add("done");
          round += 1;
          if (round === sequences.length) {
            finished = true;
            accepting = false;
            buttons.forEach((port) => (port.disabled = true));
            status.textContent = "BATTERIE 4 % — MIRACLE";
            feedback.textContent = "Assez de courant pour afficher exactement un code QR.";
            window.setTimeout(() => {
              this.finish();
              resolve({ misses });
            }, 750);
          } else {
            feedback.textContent = "Le téléphone respire. Encore une séquence.";
            window.setTimeout(() => void flashSequence(), 650);
          }
        };
      });
      void flashSequence();
    });
  }

  badgeCaptcha(): Promise<TimedGameResult> {
    const body = this.ui.openPanel(
      "CAPTCHA pour humains câblés",
      "Prouve que tu n’es pas trois switches dans un trench coat.",
    );
    const questions = [
      {
        prompt: "Clique le câble Ethernet.",
        answers: ["HDMI qui a confiance", "RJ45", "Spaghetti USB", "Fil de grille-pain"],
        correct: 1,
      },
      {
        prompt: "Le mot du jour affiché dans le hall est…",
        answers: ["ORIGNAL", "CASTOR", "ADMIN", "MOTDEPASSE"],
        correct: 1,
      },
      {
        prompt: "Que signifie AFK?",
        answers: [
          "Absent, Frigo, Ketchup",
          "Away From Keyboard",
          "Agence fédérale du clavier",
          "À finir quand?",
        ],
        correct: 1,
      },
    ];
    body.innerHTML = `
      <div class="captcha-game">
        <div class="captcha-head"><span>☑</span><div><small>JE NE SUIS PAS UN ROUTEUR</small><strong data-captcha-prompt></strong></div></div>
        <div class="captcha-grid" data-captcha-grid></div>
        <p class="game-feedback" data-feedback>Le système te regarde avec une méfiance administrative.</p>
      </div>
    `;
    const prompt = body.querySelector<HTMLElement>("[data-captcha-prompt]")!;
    const grid = body.querySelector<HTMLElement>("[data-captcha-grid]")!;
    const feedback = body.querySelector<HTMLElement>("[data-feedback]")!;
    let round = 0;
    let misses = 0;

    return new Promise((resolve) => {
      const render = (): void => {
        const question = questions[round];
        prompt.textContent = question.prompt;
        grid.innerHTML = question.answers
          .map(
            (answer, index) =>
              `<button data-answer="${index}"><i>${["🔌", "🧩", "📟", "🛰️"][index]}</i><span>${answer}</span></button>`,
          )
          .join("");
        grid.querySelectorAll<HTMLButtonElement>("[data-answer]").forEach((button) => {
          button.onclick = () => {
            if (Number(button.dataset.answer) !== question.correct) {
              misses += 1;
              this.audio.play("error");
              button.classList.add("wrong");
              button.disabled = true;
              feedback.textContent = "Réponse classée « très Wi-Fi ».";
              return;
            }
            this.audio.play("success");
            button.classList.add("right");
            grid
              .querySelectorAll<HTMLButtonElement>("button")
              .forEach((answer) => (answer.disabled = true));
            feedback.textContent = "Humanité provisoirement confirmée.";
            round += 1;
            if (round === questions.length) {
              window.setTimeout(() => {
                this.finish();
                resolve({ misses });
              }, 650);
            } else {
              window.setTimeout(render, 520);
            }
          };
        });
      };
      render();
    });
  }

  packBag(extras: PackingItem[] = []): Promise<PackingResult> {
    const body = this.ui.openPanel(
      "Opération sacoche",
      "Choisis quatre objets. Tout est fictif, absurde et inutile dans un vrai contrôle.",
    );
    const items: PackingItem[] = [
      ...extras,
      {
        id: "rhum-pouch",
        icon: "🧃",
        name: "Rhum Pouch",
        detail: "Potion de courage +3, jugement −12",
        risk: 18,
        bonus: 260,
      },
      {
        id: "mana-bleue",
        icon: "🥫",
        name: "Mana Bleue ×2",
        detail: "Fermentation arcanique non homologuée",
        risk: 12,
        bonus: 180,
      },
      {
        id: "flasque",
        icon: "📓",
        name: "Flasque « patch notes »",
        detail: "Ne contient aucun correctif logiciel",
        risk: 15,
        bonus: 220,
      },
      {
        id: "deodorant",
        icon: "🧴",
        name: "Déodorant",
        detail: "Objet rarissime en milieu LAN",
        risk: 1,
        bonus: 80,
      },
      {
        id: "eau",
        icon: "💧",
        name: "Eau réglementaire",
        detail: "Hydratation sans lore particulier",
        risk: 0,
        bonus: 40,
      },
      {
        id: "chips",
        icon: "🍟",
        name: "Croustilles alibi",
        detail: "Saveur ketchup, défense juridique faible",
        risk: 0,
        bonus: 60,
      },
    ];
    body.innerHTML = `
      <div class="packing-game">
        <div class="packing-list">
          ${items
            .map(
              (item) => `<button data-pack="${item.id}">
                <span>${item.icon}</span><div><strong>${item.name}</strong><small>${item.detail}</small></div>
                <i>SOUPÇON +${item.risk}</i>
              </button>`,
            )
            .join("")}
        </div>
        <aside class="bag-meter">
          <div><small>SACOCHE</small><strong data-bag-count>0 / 4</strong></div>
          <div class="bag-slots" data-bag-slots><i></i><i></i><i></i><i></i></div>
          <p data-risk-label>Profil actuel : étonnamment légal</p>
          <button class="button primary" data-pack-done disabled>FERMER LE SAC</button>
        </aside>
      </div>
      <p class="panel-footnote">Parodie seulement : le jeu ne reproduit ni lieu, ni procédure, ni technique réelle.</p>
    `;
    const selected = new Set<string>();
    const buttons = [...body.querySelectorAll<HTMLButtonElement>("[data-pack]")];
    const count = body.querySelector<HTMLElement>("[data-bag-count]")!;
    const slots = [...body.querySelectorAll<HTMLElement>("[data-bag-slots] i")];
    const riskLabel = body.querySelector<HTMLElement>("[data-risk-label]")!;
    const done = body.querySelector<HTMLButtonElement>("[data-pack-done]")!;

    const refresh = (): void => {
      const selectedItems = items.filter((item) => selected.has(item.id));
      const risk = selectedItems.reduce((sum, item) => sum + item.risk, 0);
      count.textContent = `${selected.size} / 4`;
      slots.forEach((slot, index) =>
        slot.classList.toggle("filled", index < selected.size),
      );
      riskLabel.textContent =
        risk < 8
          ? "Profil actuel : suspectement raisonnable"
          : risk < 28
            ? "Profil actuel : bruit de liquide inexpliqué"
            : "Profil actuel : inventaire d’un mini-boss";
      done.disabled = selected.size !== 4;
    };

    buttons.forEach((button) => {
      button.onclick = () => {
        const id = button.dataset.pack!;
        if (selected.has(id)) {
          selected.delete(id);
          button.classList.remove("selected");
        } else if (selected.size < 4) {
          selected.add(id);
          button.classList.add("selected");
        } else {
          this.audio.play("error");
          riskLabel.textContent = "La fermeture éclair invoque son délégué syndical.";
          return;
        }
        this.audio.play("click");
        refresh();
      };
    });

    return new Promise((resolve) => {
      done.onclick = () => {
        const packed = items.filter((item) => selected.has(item.id));
        this.audio.play("pickup");
        this.finish();
        resolve({
          items: packed.map((item) => item.id),
          suspicion: packed.reduce((sum, item) => sum + item.risk, 0),
          bonus: packed.reduce((sum, item) => sum + item.bonus, 0),
        });
      };
    });
  }

  /**
   * Le remplissage du sac, au frigo, avant même de partir. C'est le même geste
   * que `packBag` — choisir en sachant qu'on devra l'assumer au contrôle — mais
   * trois heures plus tôt, quand tout semble encore une bonne idée.
   */
  fillBackpack(picks: readonly PackingItem[], slots: number): Promise<PackingResult> {
    const body = this.ui.openPanel(
      "Le plein d’avant-match",
      `Le sac a ${slots} places. Ce que tu mets là, un scanner le regardera plus tard.`,
    );
    body.innerHTML = `
      <div class="packing-game">
        <div class="packing-list">
          ${picks
            .map(
              (item) => `<button data-pack="${item.id}">
                <span>${item.icon}</span><div><strong>${item.name}</strong><small>${item.detail}</small></div>
                <i>SOUPÇON +${item.risk}</i>
              </button>`,
            )
            .join("")}
        </div>
        <aside class="bag-meter">
          <div><small>SAC À DOS</small><strong data-bag-count>0 / ${slots}</strong></div>
          <div class="bag-slots" data-bag-slots>${"<i></i>".repeat(slots)}</div>
          <p data-risk-label>Le sac est vide. Ton avenir aussi, mais légalement.</p>
          <button class="button primary" data-pack-done>PARTIR AVEC ÇA</button>
        </aside>
      </div>
      <p class="panel-footnote">Parodie seulement : le jeu ne reproduit ni lieu, ni procédure, ni technique réelle.</p>
    `;

    const selected = new Set<string>();
    const buttons = [...body.querySelectorAll<HTMLButtonElement>("[data-pack]")];
    const count = body.querySelector<HTMLElement>("[data-bag-count]")!;
    const dots = [...body.querySelectorAll<HTMLElement>("[data-bag-slots] i")];
    const riskLabel = body.querySelector<HTMLElement>("[data-risk-label]")!;
    const done = body.querySelector<HTMLButtonElement>("[data-pack-done]")!;

    const refresh = (): void => {
      const chosen = picks.filter((item) => selected.has(item.id));
      const risk = chosen.reduce((sum, item) => sum + item.risk, 0);
      count.textContent = `${selected.size} / ${slots}`;
      dots.forEach((dot, index) => dot.classList.toggle("filled", index < selected.size));
      riskLabel.textContent = !selected.size
        ? "Le sac est vide. Ton avenir aussi, mais légalement."
        : risk < 6
          ? "Contenu : irréprochable, donc légèrement suspect."
          : risk < 26
            ? "Contenu : un bruit de liquide que tu devras expliquer."
            : "Contenu : de quoi faire réfléchir un bénévole entier.";
    };

    buttons.forEach((button) => {
      button.onclick = () => {
        const id = button.dataset.pack!;
        if (selected.has(id)) {
          selected.delete(id);
          button.classList.remove("selected");
        } else if (selected.size < slots) {
          selected.add(id);
          button.classList.add("selected");
        } else {
          this.audio.play("error");
          riskLabel.textContent = "La fermeture éclair a déjà donné tout ce qu’elle avait.";
          return;
        }
        this.audio.play("click");
        refresh();
      };
    });

    return new Promise((resolve) => {
      done.onclick = () => {
        const packed = picks.filter((item) => selected.has(item.id));
        this.audio.play(packed.length ? "pickup" : "click");
        this.finish();
        resolve({
          items: packed.map((item) => item.id),
          suspicion: packed.reduce((sum, item) => sum + item.risk, 0),
          bonus: packed.reduce((sum, item) => sum + item.bonus, 0),
        });
      };
    });
  }

  securityScan(): Promise<TimedGameResult> {
    const body = this.ui.openPanel(
      "Scanner de plausibilité",
      "Immobilise le signal dans la zone verte, trois fois. C’est scientifique parce que ça clignote.",
    );
    body.innerHTML = `
      <div class="scanner-game">
        <div class="scanner-arch"><i></i><span data-scan-person>🧍‍♂️</span><b></b></div>
        <div class="timing-track scan-track">
          <div class="timing-target" data-timing-target></div>
          <div class="timing-cursor" data-timing-cursor></div>
        </div>
        <div class="round-pips" data-round-pips><i></i><i></i><i></i></div>
        <button class="button primary game-action" data-stop>NE PLUS RESPIRER <kbd>ESPACE</kbd></button>
        <p class="game-feedback" data-feedback>Le scanner calibre ton aura de gamer.</p>
      </div>
    `;
    const cursor = body.querySelector<HTMLElement>("[data-timing-cursor]")!;
    const target = body.querySelector<HTMLElement>("[data-timing-target]")!;
    const feedback = body.querySelector<HTMLElement>("[data-feedback]")!;
    const person = body.querySelector<HTMLElement>("[data-scan-person]")!;
    const pips = [...body.querySelectorAll<HTMLElement>("[data-round-pips] i")];
    const button = body.querySelector<HTMLButtonElement>("[data-stop]")!;
    const targets = [42, 68, 28];
    let round = 0;
    let misses = 0;
    let active = true;
    let started = performance.now();
    let frame = 0;

    const positionTarget = (): void => {
      target.style.left = `${targets[round] - 10}%`;
      target.style.width = "20%";
    };
    const draw = (now: number): void => {
      if (!active) return;
      const value = 50 + Math.sin((now - started) * (0.003 + round * 0.0005)) * 48;
      cursor.style.left = `${value}%`;
      frame = requestAnimationFrame(draw);
    };
    const stop = (): void => {
      if (!active) return;
      const value = Number.parseFloat(cursor.style.left);
      if (Math.abs(value - targets[round]) <= 10) {
        this.audio.play("success");
        pips[round].classList.add("done");
        person.classList.add("clear");
        feedback.textContent = ["Genoux : conformes.", "Tour beige : crédible.", "Liquides : narrativement opaques."][round];
        round += 1;
        if (round === 3) {
          active = false;
          cancelAnimationFrame(frame);
          window.removeEventListener("keydown", onKey);
          window.setTimeout(() => {
            this.finish();
            resolvePromise({ misses });
          }, 750);
          return;
        }
        window.setTimeout(() => person.classList.remove("clear"), 280);
        started = performance.now();
        positionTarget();
      } else {
        misses += 1;
        this.audio.play("alert");
        person.classList.remove("clear");
        person.classList.add("flagged");
        window.setTimeout(() => person.classList.remove("flagged"), 320);
        feedback.textContent = "BIP. Tu as respiré de façon non déclarée.";
        started = performance.now();
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.code === "Space" || event.code === "KeyE") {
        event.preventDefault();
        stop();
      }
    };
    let resolvePromise!: (value: TimedGameResult) => void;
    const promise = new Promise<TimedGameResult>((resolve) => {
      resolvePromise = resolve;
    });
    positionTarget();
    button.onclick = stop;
    window.addEventListener("keydown", onKey);
    frame = requestAnimationFrame(draw);
    return promise;
  }

  untangleCable(): Promise<TimedGameResult> {
    const body = this.ui.openPanel(
      "Le nœud gordien Cat-5e",
      "Relie les fiches de même couleur. Personne ne sait pourquoi elles ont été coupées.",
    );
    const colors = ["lime", "cyan", "amber", "violet"];
    const order = [2, 0, 3, 1, 0, 2, 1, 3];
    body.innerHTML = `
      <div class="cable-game">
        <div class="cable-board">
          ${order
            .map(
              (color, index) =>
                `<button class="cable-plug ${colors[color]}" data-plug="${index}" data-color="${color}"><i></i><span>${index < 4 ? "A" : "B"}${index + 1}</span></button>`,
            )
            .join("")}
        </div>
        <div class="cable-progress"><span data-cable-progress></span></div>
        <p class="game-feedback" data-feedback>Sélectionne deux fiches identiques.</p>
      </div>
    `;
    const buttons = [...body.querySelectorAll<HTMLButtonElement>("[data-plug]")];
    const progress = body.querySelector<HTMLElement>("[data-cable-progress]")!;
    const feedback = body.querySelector<HTMLElement>("[data-feedback]")!;
    let first: HTMLButtonElement | undefined;
    let pairs = 0;
    let misses = 0;

    return new Promise((resolve) => {
      buttons.forEach((button) => {
        button.onclick = () => {
          if (button.classList.contains("matched")) return;
          if (!first) {
            first = button;
            button.classList.add("active");
            this.audio.play("click");
            return;
          }
          if (first === button) {
            first.classList.remove("active");
            first = undefined;
            return;
          }
          if (first.dataset.color === button.dataset.color) {
            this.audio.play("success");
            first.classList.remove("active");
            first.classList.add("matched");
            button.classList.add("matched");
            first = undefined;
            pairs += 1;
            progress.style.width = `${pairs * 25}%`;
            feedback.textContent = `${pairs}/4 paires. Le câble reprend goût à la vie.`;
            if (pairs === 4) {
              feedback.textContent = "Continuité électrique confirmée par la méthode du pif.";
              window.setTimeout(() => {
                this.finish();
                resolve({ misses });
              }, 700);
            }
          } else {
            misses += 1;
            this.audio.play("error");
            first.classList.remove("active");
            button.classList.add("wrong");
            window.setTimeout(() => button.classList.remove("wrong"), 260);
            first = undefined;
            feedback.textContent = "Ces deux couleurs ne partagent même pas un groupe Discord.";
          }
        };
      });
    });
  }

  seatDuel(hasProof: boolean): Promise<TimedGameResult> {
    const body = this.ui.openPanel(
      "Tribunal de la chaise 404",
      "Kevin invoque le droit ancestral du « j’étais là tantôt ».",
    );
    const rounds = [
      {
        kevin: "Mon sac de Doritos réservait clairement la place.",
        answers: [
          "Un sac n’a pas de personnalité juridique.",
          "Je reconnais la souveraineté du Dorito.",
          "On partage la chaise verticalement.",
        ],
        correct: 0,
      },
      {
        kevin: "Le numéro 404 veut dire que la place n’existe pas.",
        answers: [
          "C’est… étrangement solide.",
          "Erreur 403 : Kevin interdit.",
          "Le DHCP décidera.",
        ],
        correct: 1,
      },
      {
        kevin: "As-tu seulement une preuve?",
        answers: hasProof
          ? [
              "Capture horodatée, témoin et miettes expertisées.",
              "J’ai une intuition comptable.",
              "Gisèle est lourde.",
            ]
          : ["Mon cœur dit oui.", "Gisèle est lourde.", "Je demande un ajournement."],
        correct: hasProof ? 0 : 2,
      },
    ];
    body.innerHTML = `
      <div class="duel-game">
        <div class="duel-portraits"><span>🧔‍♂️<small>TOI</small></span><b>VS</b><span>🧀<small>KEVIN</small></span></div>
        <blockquote data-kevin-line></blockquote>
        <div class="duel-answers" data-duel-answers></div>
        <div class="round-pips" data-round-pips><i></i><i></i><i></i></div>
        <p class="game-feedback" data-feedback>Le public est composé d’un témoin AFK.</p>
      </div>
    `;
    const line = body.querySelector<HTMLElement>("[data-kevin-line]")!;
    const answers = body.querySelector<HTMLElement>("[data-duel-answers]")!;
    const feedback = body.querySelector<HTMLElement>("[data-feedback]")!;
    const pips = [...body.querySelectorAll<HTMLElement>("[data-round-pips] i")];
    let round = 0;
    let misses = 0;

    return new Promise((resolve) => {
      const render = (): void => {
        const data = rounds[round];
        line.textContent = `« ${data.kevin} »`;
        answers.innerHTML = data.answers
          .map((answer, index) => `<button data-duel="${index}">${answer}</button>`)
          .join("");
        answers.querySelectorAll<HTMLButtonElement>("[data-duel]").forEach((button) => {
          button.onclick = () => {
            if (Number(button.dataset.duel) !== data.correct) {
              misses += 1;
              this.audio.play("error");
              feedback.textContent = "Kevin gagne un point de jurisprudence imaginaire.";
              button.disabled = true;
              button.classList.add("wrong");
              return;
            }
            this.audio.play("success");
            answers
              .querySelectorAll<HTMLButtonElement>("button")
              .forEach((answer) => (answer.disabled = true));
            pips[round].classList.add("done");
            feedback.textContent = ["Objection soutenue.", "Kevin perd du Wi-Fi.", "La chaise te reconnaît."][round];
            round += 1;
            if (round === rounds.length) {
              window.setTimeout(() => {
                this.finish();
                resolve({ misses });
              }, 750);
            } else {
              window.setTimeout(render, 560);
            }
          };
        });
      };
      render();
    });
  }

  routerBoss(): Promise<TimedGameResult> {
    const body = this.ui.openPanel(
      "Gérard-DHCP, gardien du bail",
      "Route le paquet, puis répète le protocole avant l’expiration du bail.",
    );
    const route = ["WAN", "VLAN 404", "DHCP", "GISÈLE"];
    const shuffled = ["DHCP", "GISÈLE", "WAN", "VLAN 404"];
    body.innerHTML = `
      <div class="boss-game">
        <div class="boss-core"><i></i><span>GÉRARD</span><b data-boss-health></b></div>
        <div class="boss-instruction"><small>PHASE 1 — ROUTAGE</small><strong data-boss-status>Clique le trajet logique.</strong></div>
        <div class="route-nodes" data-route-nodes>
          ${shuffled.map((node) => `<button data-node="${node}">${node}</button>`).join("")}
        </div>
        <div class="protocol-grid hidden" data-protocol-grid>
          ${["lime", "cyan", "amber", "violet"]
            .map((color, index) => `<button class="${color}" data-protocol="${index}"><i></i></button>`)
            .join("")}
        </div>
        <p class="game-feedback" data-feedback>TTL : assez pour une dernière mauvaise décision.</p>
      </div>
    `;
    const routeNodes = body.querySelector<HTMLElement>("[data-route-nodes]")!;
    const protocolGrid = body.querySelector<HTMLElement>("[data-protocol-grid]")!;
    const status = body.querySelector<HTMLElement>("[data-boss-status]")!;
    const feedback = body.querySelector<HTMLElement>("[data-feedback]")!;
    const health = body.querySelector<HTMLElement>("[data-boss-health]")!;
    let routeIndex = 0;
    let misses = 0;
    let finished = false;

    return new Promise((resolve) => {
      const finishBoss = (): void => {
        finished = true;
        protocolGrid
          .querySelectorAll<HTMLButtonElement>("button")
          .forEach((button) => (button.disabled = true));
        health.style.width = "0%";
        status.textContent = "BAIL OBTENU : 192.168.0.404";
        feedback.textContent = "Gérard découvre que 404 n’est pas une adresse IPv4 valide et explose de honte.";
        this.audio.play("boot");
        window.setTimeout(() => {
          this.finish();
          resolve({ misses });
        }, 1100);
      };

      const startProtocol = async (): Promise<void> => {
        routeNodes.classList.add("hidden");
        protocolGrid.classList.remove("hidden");
        status.parentElement!.querySelector("small")!.textContent =
          "PHASE 2 — POIGNÉE DE MAIN";
        status.textContent = "Observe le protocole.";
        const sequence = [0, 2, 1, 3, 1, 0];
        const buttons = [
          ...protocolGrid.querySelectorAll<HTMLButtonElement>("[data-protocol]"),
        ];
        let input = 0;
        const show = async (): Promise<void> => {
          buttons.forEach((button) => (button.disabled = true));
          await this.wait(500);
          for (const value of sequence) {
            if (finished) return;
            buttons[value].classList.add("lit");
            this.audio.play("click");
            await this.wait(300);
            buttons[value].classList.remove("lit");
            await this.wait(110);
          }
          input = 0;
          status.textContent = "Répète la poignée de main.";
          buttons.forEach((button) => (button.disabled = false));
        };
        buttons.forEach((button) => {
          button.onclick = () => {
            const value = Number(button.dataset.protocol);
            button.classList.add("lit");
            window.setTimeout(() => button.classList.remove("lit"), 150);
            if (value !== sequence[input]) {
              misses += 1;
              this.audio.play("alert");
              feedback.textContent = "NAK. Gérard renouvelle son bail de mauvaise humeur.";
              void show();
              return;
            }
            this.audio.play("click");
            input += 1;
            health.style.width = `${50 - (input / sequence.length) * 50}%`;
            if (input === sequence.length) finishBoss();
          };
        });
        await show();
      };

      routeNodes.querySelectorAll<HTMLButtonElement>("[data-node]").forEach((button) => {
        button.onclick = () => {
          if (button.dataset.node !== route[routeIndex]) {
            misses += 1;
            this.audio.play("error");
            button.classList.add("wrong");
            window.setTimeout(() => button.classList.remove("wrong"), 300);
            feedback.textContent = "Le paquet est parti vers Laval sans consentement.";
            routeIndex = 0;
            routeNodes
              .querySelectorAll("button")
              .forEach((node) => node.classList.remove("done"));
            health.style.width = "100%";
            return;
          }
          this.audio.play("success");
          button.classList.add("done");
          routeIndex += 1;
          health.style.width = `${100 - routeIndex * 12.5}%`;
          feedback.textContent = `${routeIndex}/${route.length} sauts confirmés.`;
          if (routeIndex === route.length) void startProtocol();
        };
      });
    });
  }

  private finish(): void {
    this.ui.closePanel();
    this.afterClose();
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }
}
